import axios from 'axios-observable';
import { forkJoin, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import CONFIG from '../../settings';
import { PageableQuery, TalosPayload, UUID } from '../app/basicModels';
import { PhenomenonCategory, PhenomenonCategoryPage } from './models';
import { hasOperation, ObservationsConfigV2 } from '../observationsConfigV2';

const categoryUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/categories`;

const getPhenomenonCategoriesPage = (
  _query: PageableQuery,
  companyId: UUID,
  showCanonicals: boolean
): AxiosObservable<PhenomenonCategoryPage> => {
  if (companyId) {
    return axios.get<PhenomenonCategoryPage>(
      `${categoryUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&company_id=${companyId}&show_canonicals=${showCanonicals}`
    );
  }
  return axios.get<PhenomenonCategoryPage>(
    `${categoryUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&show_canonicals=${showCanonicals}&show_deleted=false`
  );
};

export const getPhenomenonCategories = (
  query: PageableQuery,
  companyId: UUID,
  showCanonicals: boolean
): AxiosObservable<PhenomenonCategoryPage> => {
  return getPhenomenonCategoriesPage(query, companyId, showCanonicals).pipe(
    concatMap(response => {
      const { size, total_pages } = response.data;
      const remainingPages = Array.from(
        { length: Math.max(0, total_pages - query.pageable.page - 1) },
        (_, index) => query.pageable.page + index + 1
      );
      const requests = remainingPages.map(page =>
        getPhenomenonCategoriesPage({ ...query, pageable: { ...query.pageable, page, size } }, companyId, showCanonicals)
      );

      return forkJoin([of(response), ...requests]).pipe(
        map(responses => ({
          ...response,
          data: { ...response.data, content: responses.flatMap(result => result.data.content) }
        }))
      );
    })
  );
};

export const getPhenomenonCategory = (categoryId: UUID): AxiosObservable<PhenomenonCategory> => {
  return axios.get<PhenomenonCategory>(`${categoryUrl}/by-id/${categoryId}`);
};

export const savePhenomenonCategory = (
  category: TalosPayload<PhenomenonCategory>,
  isNew: boolean,
  migratedWrites = '[]'
): AxiosObservable<PhenomenonCategory> => {
  if (isNew) {
    return createPhenomenonCategory(category, migratedWrites);
  }
  return updatePhenomenonCategory(category, migratedWrites);
};

export const createPhenomenonCategory = (
  category: TalosPayload<PhenomenonCategory>,
  migratedWrites = '[]'
): AxiosObservable<PhenomenonCategory> => {
  if (hasOperation(migratedWrites, 'phenomenon-categories.create') && ObservationsConfigV2.canWritePhenomenonCategory(category.payload)) {
    return ObservationsConfigV2.createPhenomenonCategory(category.payload);
  }

  return axios.post<PhenomenonCategory>(`${categoryUrl}`, category);
};

export const updatePhenomenonCategory = (
  category: TalosPayload<PhenomenonCategory>,
  migratedWrites = '[]'
): AxiosObservable<PhenomenonCategory> => {
  if (hasOperation(migratedWrites, 'phenomenon-categories.update') && ObservationsConfigV2.canWritePhenomenonCategory(category.payload)) {
    return ObservationsConfigV2.updatePhenomenonCategory(category.payload);
  }

  return axios.put<PhenomenonCategory>(`${categoryUrl}`, category);
};

export const deletePhenomenonCategory = (
  category: TalosPayload<PhenomenonCategory>,
  migratedWrites = '[]'
): AxiosObservable<PhenomenonCategory> => {
  if (hasOperation(migratedWrites, 'phenomenon-categories.delete') && ObservationsConfigV2.canWritePhenomenonCategory(category.payload)) {
    const { id } = category.payload;
    if (!id) throw new Error('Phenomenon category id is required');
    return ObservationsConfigV2.deletePhenomenonCategory(id);
  }

  return axios.put<PhenomenonCategory>(`${categoryUrl}/remove`, category);
};
